import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { CATEGORIES } from "../lib/brand";
import { useCart } from "../lib/cart-context";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState("all");
  const [activeSub, setActiveSub] = useState("all");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { addItem } = useCart();

  const load = async (force = false) => {
    try {
      const { data } = await axios.get(`${API}/products${force ? "?refresh=true" : ""}`);
      setProducts(data);
    } catch (e) {
      console.error(e);
      toast.error("Couldn't load products. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const refreshFromSquare = async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.post(`${API}/products/refresh`);
      toast.success(`Synced ${data.count} item${data.count === 1 ? "" : "s"} from Square`);
      await load();
    } catch (e) {
      console.error(e);
      toast.error("Square sync failed.");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      setActive(e.detail);
      setActiveSub("all");
    };
    window.addEventListener("kf:set-category", handler);
    return () => window.removeEventListener("kf:set-category", handler);
  }, []);

  // Reset subcategory whenever main category changes
  useEffect(() => {
    setActiveSub("all");
  }, [active]);

  // Subcategories visible for the active top category, in stable order
  const visibleSubcategories = useMemo(() => {
    if (active === "all") return [];
    const subs = [];
    const seen = new Set();
    for (const p of products) {
      if (p.category !== active) continue;
      const s = p.subcategory;
      if (s && !seen.has(s)) {
        seen.add(s);
        subs.push(s);
      }
    }
    return subs.sort((a, b) => a.localeCompare(b));
  }, [products, active]);

  const filtered = useMemo(() => {
    let list = products;
    if (active !== "all") list = list.filter((p) => p.category === active);
    if (activeSub !== "all") list = list.filter((p) => p.subcategory === activeSub);
    return list;
  }, [products, active, activeSub]);

  const tabs = [{ id: "all", label: "All" }, ...CATEGORIES];

  return (
    <section
      id="shop"
      data-testid="shop-section"
      className="relative bg-[#0a0a0a] py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <div className="mb-3 text-xs uppercase tracking-[0.3em] text-[#E11D2A]">Shop</div>
            <h2 className="font-display text-4xl tracking-widest text-[#F5F5DC] sm:text-5xl lg:text-6xl">
              Arm <span className="text-[#E11D2A]">Yourself</span>
            </h2>
            <p className="mt-3 max-w-xl text-sm text-[#A1A1AA] sm:text-base">
              Curated cards, board games and gear — synced live from our Square POS.
            </p>
            <button
              data-testid="refresh-square-button"
              onClick={refreshFromSquare}
              disabled={refreshing}
              className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#A1A1AA] hover:text-[#E11D2A] disabled:opacity-50"
            >
              {refreshing ? "Syncing..." : "↻ Refresh from Square"}
            </button>
          </div>

          <div data-testid="shop-tabs" className="flex flex-wrap gap-2">
            {tabs.map((t) => (
              <button
                key={t.id}
                data-testid={`shop-tab-${t.id}`}
                onClick={() => setActive(t.id)}
                className={`border px-4 py-2 text-xs uppercase tracking-widest transition-colors ${
                  active === t.id
                    ? "border-[#E11D2A] bg-[#E11D2A] text-white"
                    : "border-[#27272A] bg-transparent text-[#F5F5DC] hover:border-[#E11D2A] hover:text-[#E11D2A]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {visibleSubcategories.length > 0 && (
          <div
            data-testid="shop-subtabs"
            className="-mt-4 mb-8 flex flex-wrap gap-2 border-l-2 border-[#E11D2A] pl-3"
          >
            <button
              data-testid="shop-subtab-all"
              onClick={() => setActiveSub("all")}
              className={`px-3 py-1 text-[11px] uppercase tracking-widest transition-colors ${
                activeSub === "all"
                  ? "bg-[#E11D2A] text-white"
                  : "bg-[#121212] text-[#A1A1AA] hover:text-[#E11D2A]"
              }`}
            >
              All {tabs.find((t) => t.id === active)?.label || ""}
            </button>
            {visibleSubcategories.map((s) => (
              <button
                key={s}
                data-testid={`shop-subtab-${s.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setActiveSub(s)}
                className={`px-3 py-1 text-[11px] uppercase tracking-widest transition-colors ${
                  activeSub === s
                    ? "bg-[#E11D2A] text-white"
                    : "bg-[#121212] text-[#A1A1AA] hover:text-[#E11D2A]"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div data-testid="shop-loading" className="py-16 text-center text-[#A1A1AA]">
            Loading the dojo's inventory...
          </div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="shop-empty"
            className="border border-dashed border-[#27272A] bg-[#0f0f0f] p-12 text-center"
          >
            <div className="font-display text-3xl tracking-widest text-[#F5F5DC]">
              No products <span className="text-[#E11D2A]">yet</span>
            </div>
            <p className="mx-auto mt-3 max-w-md text-sm text-[#A1A1AA]">
              Add items in your Square POS — assign them to a category named
              <span className="text-[#F5F5DC]"> "TCG"</span>,
              <span className="text-[#F5F5DC]"> "Board Games"</span>, or
              <span className="text-[#F5F5DC]"> "Accessories"</span> — then click
              "Refresh from Square" above to sync.
            </p>
          </div>
        ) : (
          <div
            data-testid="shop-grid"
            className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          >
            {filtered.map((p) => (
              <article
                key={p.id}
                data-testid={`product-card-${p.id}`}
                className="group flex flex-col border border-[#27272A] bg-[#121212] transition-colors hover:border-[#E11D2A]"
              >
                <div className="relative overflow-hidden">
                  <div
                    className="aspect-[4/3] w-full bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                    style={{ backgroundImage: `url(${p.image})` }}
                  />
                  <div className="absolute left-3 top-3 border border-[#E11D2A]/40 bg-black/60 px-2 py-1 text-[10px] uppercase tracking-widest text-[#E11D2A]">
                    {p.category_label}
                    {p.subcategory ? ` · ${p.subcategory}` : ""}
                  </div>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="font-display text-xl tracking-wider text-[#F5F5DC]">
                    {p.name}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-sm text-[#A1A1AA]">{p.description}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <div className="font-display text-2xl tracking-wider text-[#E11D2A]">
                      ${p.price.toFixed(2)}
                    </div>
                    <Button
                      data-testid={`add-to-cart-${p.id}-button`}
                      onClick={() => {
                        addItem(p, 1);
                        toast.success(`${p.name} added to cart`);
                      }}
                      className="rounded-none bg-[#E11D2A] px-4 py-2 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#F02D3A]"
                    >
                      Add to Cart
                    </Button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
